// Copyright (c) StrangeBee 2024

import React, { ReactElement, useEffect, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THColorPicker } from '@Molecules/Inputs/THColorPicker/THColorPicker';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { DescribeFieldOutput, CustomStatusMapType, CustomStatusOutput, AlertStatusStage } from 'thehive-sdk';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';

import './CustomStatusForm.scss';

export interface CustomStatusValuesForm {
    stage: string;
    value: string;
    colour?: string;
}

interface CustomStatusFormProps {
    onFinish(values: CustomStatusValuesForm): void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    describeField?: DescribeFieldOutput;
    data?: CustomStatusOutput;
    statusList?: CustomStatusMapType;
    onCancel: () => void;
}

export const CustomStatusForm = ({
    onFinish,
    form,
    isValid,
    onChange,
    describeField,
    data,
    statusList,
    onCancel,
}: CustomStatusFormProps): ReactElement => {
    const i = useLocalMessage();

    const [colourPreview, setColourPreview] = React.useState(data?.colour || '#d0021b');
    const [valuePreview, setValuePreview] = React.useState(data?.value || 'value');
    const [statusAvailable, setStatusAvailable] = React.useState<Record<string, string[]>>({});
    const stageAttribute = useMemo(
        () =>
            describeField?.attributes
                ?.find(({ name }) => name === 'stage')
                ?.values.filter((val) => val !== AlertStatusStage.IMPORTED) || [],
        [describeField],
    );
    const updateMode = !!(data && Object.keys(data).length);

    useEffect(() => {
        if (statusList) {
            const statusMap = Object.entries(statusList).reduce<Record<string, string[]>>(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: [...(acc[key] || []), ...value.map(({ value }) => value)],
                }),
                {},
            );
            setStatusAvailable(statusMap);
        }
    }, [statusList]);

    const onChangeHandler = (obj: any) => {
        if (obj.colour) {
            setColourPreview(obj.colour);
        }
        if (obj.value) {
            setValuePreview(obj.value);
        }
        onChange();
    };

    return (
        <THForm<CustomStatusValuesForm>
            name="custom-resolution-status-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChangeHandler}
            initialValues={data}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={!data ? i(`custom-status.create.submit`) : i(`custom-status.edit.submit`)}
                    />
                }
            >
                <THForm.Item
                    label={i('custom-status.form.stage.label')}
                    name="stage"
                    rules={[{ required: true, message: i('custom-status.form.stage.required') }]}
                >
                    <THSelect
                        options={stageAttribute.map((value) => ({ label: value, value }))}
                        disabled={updateMode}
                    />
                </THForm.Item>

                <THForm.Item
                    label={i('custom-status.form.value.label')}
                    name="value"
                    rules={[
                        { required: true, message: i('custom-status.form.value.required') },
                        {
                            message: i('custom-status.form.value.already-exist'),
                            validator: (_, value) => {
                                const stage = form.getFieldValue('stage');
                                if (
                                    data?.value === value ||
                                    (statusAvailable?.[stage] && !statusAvailable?.[stage].includes(value))
                                ) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject();
                                }
                            },
                        },
                    ]}
                >
                    <THInput placeholder={i('custom-status.form.value.placeholder')} disabled={updateMode} />
                </THForm.Item>

                <THForm.Item
                    label={i('custom-status.form.colour.label')}
                    name="colour"
                    rules={[
                        () => ({
                            validator: (_, value) => {
                                if (!value || (value.startsWith('#') && (value.length === 7 || value.length === 4))) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(i('custom-status.form.colour.format')));
                            },
                        }),
                    ]}
                >
                    <THColorPicker />
                </THForm.Item>
                <span className="form-custom__preview-label">{i('custom-status.form.preview.label')}</span>
                <div className="form-custom__preview">
                    <ColorLabel
                        color={colourPreview}
                        label={valuePreview}
                        className="form-custom__color-label status-tag"
                    />
                </div>
            </THForm.Layout>
        </THForm>
    );
};
